import React, { useCallback, useEffect } from 'react';
import checkClick from '@src/ds/Utils/CheckClick';

export type AIButtonProps = {
  enabled: boolean;
  code: string;
  aiProducts: string;
  onLoadAI: () => void;
  openEbook: () => void;
  openMindMap: () => void;
  openQuiz: () => void;
  openSummaryText: () => void;
  hasAIOpened: boolean;
};

const AIButton = ({ enabled, code, aiProducts, onLoadAI, openEbook, openMindMap, openQuiz, openSummaryText, hasAIOpened }: AIButtonProps) => {
  const [hasAIProducts, setHasAIProducts] = React.useState<boolean>(false);
  const [currentAIProducts, setCurrentAIProducts] = React.useState<{
    quiz: boolean;
    mindMap: boolean;
    ebook: boolean;
    summaryText: boolean;
  }>({
    quiz: false,
    mindMap: false,
    ebook: false,
    summaryText: false,
  });

  const checkAfterClick = useCallback((event: any) => {
    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;

    if (checkClick(event)) {
      rootElement.querySelector('.smartplayer__ai')?.classList.remove('smartplayer__ai--opened');
    }
  }, [code]);

  const onAIOpen = useCallback((event: any) => {
    event.preventDefault();
    event.stopPropagation();

    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;

    const volumeButton = rootElement.querySelector('.smartplayer__volume') as HTMLButtonElement;

    if (volumeButton) {
      volumeButton.classList.remove('smartplayer__volume--opened');
    }

    const configurationButton = rootElement.querySelector('.smartplayer__configuration') as HTMLButtonElement;

    if (configurationButton) {
      configurationButton.classList.remove('smartplayer__configuration--opened');
      configurationButton.classList.remove('smartplayer__quality--opened');
      configurationButton.classList.remove('smartplayer__velocity--opened');
      configurationButton.classList.remove('smartplayer__subtitle--opened');
    }

    let isOpened = event.currentTarget?.parentElement?.classList.toggle('smartplayer__ai--opened');

    if (isOpened) {
      onLoadAI();
      // rootElement.querySelector('.smartplayer__footer')?.classList.add('smartplayer__footer--dropdown');
    }
    // else if (!hasAIOpened) {
    // rootElement.querySelector('.smartplayer__footer')?.classList.remove('smartplayer__footer--dropdown');
    // }

  }, [code, onLoadAI]);

  const openAIProduct = useCallback((product: string) => () => {
    switch (product) {
      case 'ebook':
        openEbook();
        break;
      case 'mindMap':
        openMindMap();
        break;
      case 'quiz':
        openQuiz();
        break;
      case 'summaryText':
        openSummaryText();
        break;
    }

    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;
    rootElement.querySelector('.smartplayer__ai')?.classList.remove('smartplayer__ai--opened');
    // rootElement.querySelector('.smartplayer__footer')?.classList.add('smartplayer__footer--dropdown');
  }, [code, openEbook, openMindMap, openQuiz, openSummaryText]);

  useEffect(() => {
    if (aiProducts) {
      const aiProductsArr = aiProducts.split(',');

      setCurrentAIProducts({
        quiz: aiProductsArr.indexOf('QUESTION') !== -1,
        ebook: aiProductsArr.indexOf('SUMMARY') !== -1,
        mindMap: aiProductsArr.indexOf('MIND_MAP') !== -1,
        summaryText: aiProductsArr.indexOf('SUMMARY_TEXT') !== -1,
      })

      setHasAIProducts(true);
    }
  }, [aiProducts]);

  if (!hasAIProducts || (!currentAIProducts.ebook && !currentAIProducts.mindMap && !currentAIProducts.quiz)) {
    return null;
  }

  return enabled ? (
    <div className="smartplayer__ai" onClick={checkAfterClick}>
      <button className="smartplayer__bar-button" type="button" onClick={onAIOpen}>
        <div className="icon icon--ai">
          <svg xmlns="http://www.w3.org/2000/svg" height="32" width="32" viewBox="0 0 512 512" fill="currentColor">
            <path d="M176 24c0-13.3-10.7-24-24-24s-24 10.7-24 24V64c-35.3 0-64 28.7-64 64H24c-13.3 0-24 10.7-24 24s10.7 24 24 24H64v56H24c-13.3 0-24 10.7-24 24s10.7 24 24 24H64v56H24c-13.3 0-24 10.7-24 24s10.7 24 24 24H64c0 35.3 28.7 64 64 64v40c0 13.3 10.7 24 24 24s24-10.7 24-24V448h56v40c0 13.3 10.7 24 24 24s24-10.7 24-24V448h56v40c0 13.3 10.7 24 24 24s24-10.7 24-24V448c35.3 0 64-28.7 64-64h40c13.3 0 24-10.7 24-24s-10.7-24-24-24H448V280h40c13.3 0 24-10.7 24-24s-10.7-24-24-24H448V176h40c13.3 0 24-10.7 24-24s-10.7-24-24-24H448c0-35.3-28.7-64-64-64V24c0-13.3-10.7-24-24-24s-24 10.7-24 24V64H280V24c0-13.3-10.7-24-24-24s-24 10.7-24 24V64H176V24zm32 152c6.4 0 12.1 3.8 14.7 9.6l42 96 14 32c3.5 8.1-.1 17.5-8.2 21.1s-17.5-.2-21.1-8.2L239.5 304H176.5l-9.8 22.4c-3.5 8.1-13 11.8-21.1 8.2s-11.8-13-8.2-21.1l14-32 42-96c2.5-5.8 8.3-9.6 14.7-9.6zm17.5 96L208 231.9 190.5 272h35.1zM336 176c8.8 0 16 7.2 16 16V320c0 8.8-7.2 16-16 16s-16-7.2-16-16V192c0-8.8 7.2-16 16-16z"/>
          </svg>
        </div>
      </button>
      <div className="smartplayer__dropdownContainer">
        <div className="smartplayer__aiList">
          {
            currentAIProducts.summaryText ?
            (<div className="smartplayer__aiItem">
              <input type="radio" id="summaryText" name="ai" value="summaryText" />
              <label htmlFor="summaryText" onClick={openAIProduct('summaryText')}>Resumo<span className="smartplayer__aiItemBox"></span></label>
            </div>) : null
          }
          {
            currentAIProducts.ebook ?
            (<div className="smartplayer__aiItem">
              <input type="radio" id="ebook" name="ai" value="ebook" />
              <label htmlFor="ebook" onClick={openAIProduct('ebook')}>E-Book<span className="smartplayer__aiItemBox"></span></label>
            </div>) : null
          }
          {
            currentAIProducts.mindMap ?
            (<div className="smartplayer__aiItem">
              <input type="radio" id="mindMap" name="ai" value="mindMap" />
              <label htmlFor="mindMap" onClick={openAIProduct('mindMap')}>Mapa Mental<span className="smartplayer__aiItemBox"></span></label>
            </div>) : null
          }
          {
            currentAIProducts.quiz ?
            (<div className="smartplayer__aiItem">
              <input type="radio" id="quiz" name="ai" value="quiz" />
              <label htmlFor="quiz" onClick={openAIProduct('quiz')}>Perguntas<span className="smartplayer__aiItemBox"></span></label>
            </div>) : null
          }
        </div>
      </div>
    </div>
  ) : null;
};

export default AIButton;
